import { useState } from "react";
import PropTypes from "prop-types";
import MenuItem from "@mui/material/MenuItem";
import Switch from "@mui/material/Switch";
import FormControlLabel from "@mui/material/FormControlLabel";
import Alert from "@mui/material/Alert";
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";

import MDInput from "components/MDInput";
import ActionDialog from "components/admin/ActionDialog";
import { useFetch } from "components/admin/hooks";
import api from "api/client";
import { formatNumber } from "utils/format";
import { dialogProps as baseProps, useSubmit } from "layouts/players/PlayerActions";

const itemShape = PropTypes.shape({
  itemId: PropTypes.number.isRequired,
  name: PropTypes.string.isRequired,
  count: PropTypes.number,
  lvl: PropTypes.number,
  stackable: PropTypes.bool,
  canTrade: PropTypes.bool,
  numFusion: PropTypes.number,
  petEquipId: PropTypes.number,
});

const itemTitle = (item) => `${item.name}${item.lvl > 0 ? ` +${item.lvl}` : ""} (#${item.itemId})`;

export function RemoveItemDialog({ userId, inventory, item, onClose, onDone }) {
  const [submit, busy] = useSubmit(onClose, onDone);
  const [count, setCount] = useState(item.count || 1);
  const [reason, setReason] = useState("");
  const equipped = item.petEquipId > 0;
  return (
    <ActionDialog
      open
      title={`Thu hồi ${itemTitle(item)}`}
      color="error"
      submitText="Thu hồi"
      busy={busy}
      disabled={equipped || (item.stackable && Number(count) <= 0)}
      onClose={onClose}
      onSubmit={() =>
        submit(() =>
          api.post(`/api/admin/players/${userId}/items/remove`, {
            inventory,
            itemId: item.itemId,
            count: item.stackable ? Number(count) : 0,
            reason,
          })
        )
      }
    >
      {equipped && (
        <Alert severity="warning">
          Món này đang mặc trên pet #{item.petEquipId}, người chơi phải tháo ra trước.
        </Alert>
      )}
      {item.stackable && (
        <MDInput
          type="number"
          label={`Số lượng thu hồi (đang có ${formatNumber(item.count)})`}
          value={count}
          inputProps={{ min: 1, max: item.count }}
          onChange={(e) => setCount(e.target.value)}
        />
      )}
      <MDInput
        label="Lý do (ghi vào lịch sử)"
        value={reason}
        onChange={(e) => setReason(e.target.value)}
      />
      <Alert severity="info">
        Vật phẩm bị xoá hẳn, không trả về đâu cả. Người chơi đang online sẽ nhận thông báo.
      </Alert>
    </ActionDialog>
  );
}
RemoveItemDialog.propTypes = {
  ...baseProps,
  inventory: PropTypes.number.isRequired,
  item: itemShape.isRequired,
};

export function EditItemDialog({ userId, inventory, item, onClose, onDone }) {
  const [submit, busy] = useSubmit(onClose, onDone);
  const [canTrade, setCanTrade] = useState(!!item.canTrade);
  const [numFusion, setNumFusion] = useState(item.numFusion || 0);
  return (
    <ActionDialog
      open
      title={`Sửa ${itemTitle(item)}`}
      description="Khoá hoặc mở giao dịch món này; với trang bị thì đặt được số sao dung hợp."
      color="warning"
      submitText="Lưu"
      busy={busy}
      onClose={onClose}
      onSubmit={() =>
        submit(() =>
          api.post(`/api/admin/players/${userId}/items/update`, {
            inventory,
            itemId: item.itemId,
            canTrade,
            numFusion: item.stackable ? null : Number(numFusion),
          })
        )
      }
    >
      <FormControlLabel
        control={<Switch checked={canTrade} onChange={(e) => setCanTrade(e.target.checked)} />}
        label={canTrade ? "Giao dịch được" : "Khoá giao dịch"}
      />
      {!item.stackable && (
        <MDInput
          type="number"
          label="Số sao dung hợp (chỉ trang bị dung hợp được)"
          value={numFusion}
          inputProps={{ min: 0 }}
          onChange={(e) => setNumFusion(e.target.value)}
        />
      )}
    </ActionDialog>
  );
}
EditItemDialog.propTypes = {
  ...baseProps,
  inventory: PropTypes.number.isRequired,
  item: itemShape.isRequired,
};

export function EditPetDialog({ userId, pet, onClose, onDone }) {
  const [submit, busy] = useSubmit(onClose, onDone);
  const [form, setForm] = useState({
    lvl: pet.lvl,
    star: pet.star,
    tiemnangPoint: pet.tiemnangPoint || 0,
    skillPoint: pet.skillPoint || 0,
  });
  const field = (key, label, props = {}) => (
    <Grid item xs={6}>
      <MDInput
        fullWidth
        type="number"
        label={label}
        value={form[key]}
        inputProps={props}
        onChange={(e) => setForm({ ...form, [key]: e.target.value })}
      />
    </Grid>
  );
  return (
    <ActionDialog
      open
      title={`Sửa pet ${pet.name} (#${pet.petId})`}
      description="Sửa cấp, sao, điểm tiềm năng chưa chia và điểm kỹ năng của pet này."
      color="warning"
      submitText="Lưu"
      busy={busy}
      onClose={onClose}
      onSubmit={() =>
        submit(() =>
          api.post(`/api/admin/players/${userId}/pets/update`, {
            petId: pet.petId,
            lvl: Number(form.lvl),
            star: Number(form.star),
            tiemnangPoint: Number(form.tiemnangPoint),
            skillPoint: Number(form.skillPoint),
          })
        )
      }
    >
      <Grid container spacing={2}>
        {field("lvl", "Cấp (1 - 999)", { min: 1, max: 999 })}
        {field("star", "Sao (0 - 5)", { min: 0, max: 5 })}
        {field("tiemnangPoint", "Điểm tiềm năng chưa chia", { min: 0 })}
        {field("skillPoint", "Điểm kỹ năng", { min: 0 })}
      </Grid>
      <Alert severity="info">
        Chỉ số sức / nhanh / trí không đổi theo cấp; người chơi dùng điểm tiềm năng để tăng.
      </Alert>
    </ActionDialog>
  );
}
EditPetDialog.propTypes = {
  ...baseProps,
  pet: PropTypes.shape({
    petId: PropTypes.number.isRequired,
    name: PropTypes.string,
    lvl: PropTypes.number,
    star: PropTypes.number,
    tiemnangPoint: PropTypes.number,
    skillPoint: PropTypes.number,
  }).isRequired,
};

export function AchievementDialog({ userId, onClose, onDone }) {
  const [submit, busy] = useSubmit(onClose, onDone);
  const list = useFetch(() => api.get("/api/admin/templates/achievements"), []);
  const [selected, setSelected] = useState(null);
  const [days, setDays] = useState(0);
  return (
    <ActionDialog
      open
      title="Cấp danh hiệu"
      description="Thêm danh hiệu vào danh sách của người chơi, có thể đặt số ngày hết hạn."
      submitText="Cấp"
      busy={busy}
      disabled={!selected}
      onClose={onClose}
      onSubmit={() =>
        submit(() =>
          api.post(`/api/admin/players/${userId}/achievements`, {
            templateId: selected.id,
            days: Number(days),
          })
        )
      }
    >
      <Autocomplete
        options={list.data || []}
        loading={list.loading}
        value={selected}
        onChange={(e, value) => setSelected(value)}
        getOptionLabel={(a) => `#${a.id} ${a.name}`}
        isOptionEqualToValue={(a, b) => a.id === b.id}
        renderInput={(params) => <MDInput {...params} label="Danh hiệu" />}
      />
      <MDInput
        type="number"
        label="Số ngày (0 = vĩnh viễn)"
        value={days}
        inputProps={{ min: 0 }}
        onChange={(e) => setDays(e.target.value)}
      />
    </ActionDialog>
  );
}
AchievementDialog.propTypes = baseProps;

export function TeleportDialog({ userId, onClose, onDone }) {
  const [submit, busy] = useSubmit(onClose, onDone);
  const maps = useFetch(() => api.get("/api/admin/templates/maps"), []);
  const [map, setMap] = useState(null);
  const [zoneId, setZoneId] = useState("");
  return (
    <ActionDialog
      open
      title="Dịch chuyển người chơi"
      submitText="Chuyển"
      busy={busy}
      disabled={!map}
      onClose={onClose}
      onSubmit={() =>
        submit(() =>
          api.post(`/api/admin/players/${userId}/teleport`, {
            mapId: map.mapId,
            zoneId: zoneId === "" ? null : zoneId,
          })
        )
      }
    >
      <Autocomplete
        options={maps.data || []}
        loading={maps.loading}
        value={map}
        onChange={(e, value) => {
          setMap(value);
          setZoneId("");
        }}
        getOptionLabel={(m) => `#${m.mapId} ${m.name}`}
        isOptionEqualToValue={(a, b) => a.mapId === b.mapId}
        renderInput={(params) => <MDInput {...params} label="Map" />}
      />
      <MDInput
        select
        label="Khu"
        value={zoneId}
        disabled={!map}
        onChange={(e) => setZoneId(e.target.value)}
        SelectProps={{ sx: { height: 44 } }}
      >
        <MenuItem value="">Khu còn chỗ bất kỳ</MenuItem>
        {(map?.zones || []).map((z) => (
          <MenuItem key={z} value={z}>
            Khu {z}
          </MenuItem>
        ))}
      </MDInput>
      <Alert severity="info">
        Người chơi phải đang online và không trong trận đánh. Chỉ chuyển vào khu thường của map.
      </Alert>
    </ActionDialog>
  );
}
TeleportDialog.propTypes = baseProps;
